import type { ReactElement, ReactNode } from 'react'

import { Anchor } from '../components/Anchor/Anchor'
import { Bold } from '../components/Bold/Bold'
import { ClientButton } from '../components/ClientButton/ClientButton'
import { Container } from '../components/Container/Container'
import { Image } from '../components/Image/Image'
import { Italic } from '../components/Italic/Italic'
import { createListElement } from '../components/List/List'
import { Paragraph } from '../components/Paragraph/Paragraph'
import { Section } from '../components/Section/Section'
import { Subtitle } from '../components/Subtitle/Subtitle'
import { createTableElement } from '../components/Table/Table'
import { Title } from '../components/Title/Title'
import { Underline } from '../components/Underline/Underline'
import { HTML_TAGS_REGEX, PROPS_REGEX } from '../constants/regex'
import type { CreateComponentsConfig } from '../types'

const SELF_CLOSING_TAG_REGEX = /\/\s*>/
const SPLIT_TAG_BEGIN_REGEX = /\s|>/

// Index constants for regex match groups
const completeMatchIndex = 0
const regexTextIndex = 1

// Tag begin literals
const boldBegin = '<b'
const strongBegin = '<strong'

const italicBegin = '<i'
const underlineBegin = '<u'

const linkBegin = '<a'
const regexLinkNameIndex = 1
const regexLinkTextIndex = 1

const titleBegin = '<h2'

const subtitleBegin = '<h3'

const paragraphBegin = '<p'

const buttonBegin = '<button'

const tableBegin = '<table'
const tableBodyBegin = '<tbody'
const tableHeadBegin = '<thead'
const tableThBegin = '<th'
const tableTrBegin = '<tr'
const tableTdBegin = '<td'

const listUlBegin = '<ul'
const listOlBegin = '<ol'
const listLiBegin = '<li'

const containerBegin = '<div'
const sectionBegin = '<section'

const imageBegin = '<img'
const linebreakBegin = '<br'

function getMatch(regex: RegExp, match: RegExpExecArray) {
	regex.lastIndex = 0
	const linkMatch = regex.exec(match[completeMatchIndex])
	return linkMatch?.[regexLinkNameIndex] ?? ''
}

function createBoldComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const customBoldProps = config.boldProps
	return (
		<Bold key={`b-${counter}`} customProps={customBoldProps}>
			{match[regexTextIndex]}
		</Bold>
	)
}

function createAnchorComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const href = getMatch(PROPS_REGEX.hrefProp, match)
	const link = getMatch(PROPS_REGEX.linkProp, match)
	const target = getMatch(PROPS_REGEX.targetProp, match)
	const customLinkProps = { target, ...config.linkProps }
	const { isClient } = config
	return (
		<Anchor
			key={`a-${counter}`}
			href={href || link}
			isClient={isClient}
			customProps={customLinkProps}
		>
			{createComponents(match[regexLinkTextIndex], config)}
		</Anchor>
	)
}

function createItalicComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const customItalicProps = config.italicProps
	return (
		<Italic key={`i-${counter}`} customProps={customItalicProps}>
			{match[regexTextIndex]}
		</Italic>
	)
}

function createTitleComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const customTitleProps = config.titleProps
	return (
		<Title key={`title-${counter}`} customProps={customTitleProps}>
			{createComponents(match[regexTextIndex], config)}
		</Title>
	)
}

function createSubtitleComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const customSubtitleProps = config.subtitleProps
	return (
		<Subtitle key={`subtitle-${counter}`} customProps={customSubtitleProps}>
			{createComponents(match[regexTextIndex], config)}
		</Subtitle>
	)
}

function createParagraphComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const customProps = config.paragraphProps
	return (
		<Paragraph key={`p-${counter}`} customProps={customProps}>
			{createComponents(match[regexTextIndex], config)}
		</Paragraph>
	)
}

function createButtonComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray,
	buttonCounter: number
) {
	const customButtonProps = config.buttonProps
		? config.buttonProps[buttonCounter]
		: {}

	return (
		<ClientButton key={`btn-${counter}`} {...customButtonProps}>
			{match[regexTextIndex]}
		</ClientButton>
	)
}

function createUnderlineComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const customUnderlineProps = config.underlineProps
	return (
		<Underline key={`u-${counter}`} customProps={customUnderlineProps}>
			{createComponents(match[regexTextIndex], config)}
		</Underline>
	)
}

function createTableComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray,
	tag: string
) {
	const HTMLTag = tag.replace('<', '')
	const customTableProps = config.tableProps
	const key = `${HTMLTag}-${counter}`
	return createTableElement(
		key,
		HTMLTag,
		createComponents(match[regexTextIndex], config),
		customTableProps
	)
}

function createListComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray,
	tag: string
) {
	const HTMLTag = tag.replace('<', '')
	const customListProps = {
		...config.listProps,
		...(tag === listLiBegin && { key: counter }),
	}

	const key = `${HTMLTag}-${counter}`
	return createListElement(
		key,
		HTMLTag,
		createComponents(match[regexTextIndex], config),
		customListProps
	)
}

function createContainerComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const className = getMatch(PROPS_REGEX.classProp, match)
	const customContainerProps = { className, ...config.containerProps }
	return (
		<Container key={`div-${counter}`} customProps={customContainerProps}>
			{createComponents(match[regexTextIndex], config)}
		</Container>
	)
}

function createSectionComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const className = getMatch(PROPS_REGEX.classProp, match)
	const customSectionProps = { className, ...config.sectionProps }
	return (
		<Section key={`section-${counter}`} customProps={customSectionProps}>
			{createComponents(match[regexTextIndex], config)}
		</Section>
	)
}

function createImageComponent(
	config: CreateComponentsConfig,
	counter: number,
	match: RegExpExecArray
) {
	const src = getMatch(PROPS_REGEX.srcProp, match)
	const alt = getMatch(PROPS_REGEX.altProp, match)
	const customImageProps = { src, alt, ...config.imageProps }

	return (
		<Image
			key={`img-${counter}`}
			src={src}
			alt={alt}
			customProps={customImageProps}
		/>
	)
}

function createLineBreakComponent() {
	return <br />
}

/** Returns the proper component depending on the match parameter. */
const createComponent = (
	/** The target string that will be replaced by the component */
	match: RegExpExecArray,
	/** The config object used to add props to the components */
	config: CreateComponentsConfig,
	/** Used to create the unique key */
	counter = 0,
	/** Button-specific counter for buttonProps alignment */
	buttonCounter = 0
) => {
	const cleanedMatch = match[0].replace(SELF_CLOSING_TAG_REGEX, '>')
	const beginWith = cleanedMatch.split(SPLIT_TAG_BEGIN_REGEX)[0]

	const componentCreators: Record<string, () => React.JSX.Element> = {
		[boldBegin]: () => createBoldComponent(config, counter, match),
		[strongBegin]: () => createBoldComponent(config, counter, match),
		[linkBegin]: () => createAnchorComponent(config, counter, match),
		[italicBegin]: () => createItalicComponent(config, counter, match),
		[titleBegin]: () => createTitleComponent(config, counter, match),
		[subtitleBegin]: () => createSubtitleComponent(config, counter, match),
		[paragraphBegin]: () => createParagraphComponent(config, counter, match),
		[buttonBegin]: () =>
			createButtonComponent(config, counter, match, buttonCounter),
		[underlineBegin]: () => createUnderlineComponent(config, counter, match),
		[containerBegin]: () => createContainerComponent(config, counter, match),
		[sectionBegin]: () => createSectionComponent(config, counter, match),
		[imageBegin]: () => createImageComponent(config, counter, match),
		[linebreakBegin]: () => createLineBreakComponent(),
		[tableBegin]: () =>
			createTableComponent(config, counter, match, tableBegin),
		[tableBodyBegin]: () =>
			createTableComponent(config, counter, match, tableBodyBegin),
		[tableHeadBegin]: () =>
			createTableComponent(config, counter, match, tableHeadBegin),
		[tableThBegin]: () =>
			createTableComponent(config, counter, match, tableThBegin),
		[tableTrBegin]: () =>
			createTableComponent(config, counter, match, tableTrBegin),
		[tableTdBegin]: () =>
			createTableComponent(config, counter, match, tableTdBegin),
		[listUlBegin]: () =>
			createListComponent(config, counter, match, listUlBegin),
		[listOlBegin]: () =>
			createListComponent(config, counter, match, listOlBegin),
		[listLiBegin]: () =>
			createListComponent(config, counter, match, listLiBegin),
	}
	return { component: componentCreators[beginWith](), componentType: beginWith }
}

function getFirstOccurrenceOf(regexList: RegExp[], literal: string) {
	return regexList.reduce(
		(lastMatch: RegExpExecArray | null, regexp: RegExp) => {
			regexp.lastIndex = 0
			const current = regexp.exec(literal)
			let result = current ?? lastMatch
			if (current && lastMatch) {
				result = current?.index < lastMatch?.index ? current : lastMatch
			}
			return result
		},
		null
	)
}

/** Transforms from a literal to a valid React.ReactNode with the proper parsed sub-elements */
export const createComponents = (
	/** The target string that will be replaced by the component */
	literalValue: string,
	/** The config object used to add props to the components */
	config: CreateComponentsConfig = {},
	/** Output component array used in the recursive calls */
	renderList: ReactNode[] = [],
	/** Unique number to create the key */
	counter = 0,
	/** Button-specific counter for buttonProps alignment */
	buttonCounter = 0
): ReactElement => {
	// Matches any <h2>, <h3>, <a>, <p>, <b>, <i>, <button>, <strong>, <u>, <table>,
	// <tbody>, <thead>, <th>, <tr>, <td>, <ul>, <ol>, <li>, <div>, <section>, <img>
	// tags

	const match = getFirstOccurrenceOf(HTML_TAGS_REGEX, literalValue)

	if (match) {
		renderList.push(literalValue.substring(0, match.index))
		const { component, componentType } = createComponent(
			match,
			config,
			counter,
			buttonCounter
		)
		renderList.push(component)

		const suffix = literalValue.substring(
			match.index + match[completeMatchIndex].length,
			literalValue.length
		)

		const isButton = componentType === buttonBegin

		return createComponents(
			suffix,
			config,
			renderList,
			counter + 1,
			buttonCounter + (isButton ? 1 : 0)
		)
	}
	if (literalValue) {
		renderList.push(literalValue)
	}
	// Fragment needed to transform the array into a valid React.ReactNode
	return <>{renderList}</>
}
